// aiEditorFeatures.js - Monaco AI behavior for the editor
// - Registers inline GPT-5.1 Codex completions for JavaScript via getCodeCompletion
// - Adds context menu actions (Explain / Fix / Optimize) that call explainCode
// Editor.jsx passes in UI callbacks (modal + toasts); this file should stay UI-agnostic.

/**
 * Get code completion from GPT-5.1 Codex
 * Uses 'none' reasoning mode for fast completions
 */
async function getCodeCompletion(beforeCursor, afterCursor, language) {
  try {
    const response = await fetch('/api/ai-chat', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        messages: [
          {
            role: 'system',
            content: 'You are a p5.js code completion assistant. Provide ONLY the code completion, no explanations. Complete the code naturally following best practices for editor.p5js.org. For three.js, use r147 global scripts (no ES6 modules).'
          },
          {
            role: 'user',
            content: `Complete this ${language} code:\n\nCode before cursor:\n${beforeCursor}\n\nCode after cursor:\n${afterCursor}\n\nProvide ONLY the missing code to insert at the cursor position.`
          }
        ],
        provider: 'openai',
        model: 'gpt-5.1-codex',
        reasoningMode: 'none' // Fast completions, no reasoning
      })
    })

    if (!response.ok) return null

    // Parse SSE stream for completion
    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let completion = ''
    let buffer = ''

    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() || ''

      for (const line of lines) {
        if (!line.trim() || !line.startsWith('data: ')) continue
        const dataStr = line.slice(6)
        if (dataStr === '[DONE]') continue

        try {
          const data = JSON.parse(dataStr)
          if (data.type === 'delta' && data.text) {
            completion += data.text
          }
        } catch (e) {
          // Skip parse errors
        }
      }
    }

    return completion.trim() || null
  } catch (error) {
    console.error('Code completion error:', error)
    return null
  }
}

/**
 * Explain, fix, or optimize code using GPT-5.1 Codex
 * Uses 'low' reasoning mode for better analysis
 */
async function explainCode(code, action = 'explain') {
  const prompts = {
    explain: `Explain this p5.js code in simple terms. What does it do and how does it work?\n\nCode:\n${code}`,
    fix: `Analyze this p5.js code for bugs and improvements. Provide specific fixes and better practices.\n\nCode:\n${code}`,
    optimize: `Optimize this p5.js code for better performance and readability. Suggest improvements.\n\nCode:\n${code}`
  }

  try {
    const response = await fetch('/api/ai-chat', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        messages: [
          {
            role: 'system',
            content: 'You are a p5.js code analysis expert. Provide clear, actionable insights about the code.'
          },
          {
            role: 'user',
            content: prompts[action]
          }
        ],
        provider: 'openai',
        model: 'gpt-5.1-codex',
        reasoningMode: 'low' // Some reasoning for better analysis
      })
    })

    if (!response.ok) {
      throw new Error('AI analysis failed')
    }

    // Parse SSE stream for response
    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let result = ''
    let buffer = ''

    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() || ''

      for (const line of lines) {
        if (!line.trim() || !line.startsWith('data: ')) continue
        const dataStr = line.slice(6)
        if (dataStr === '[DONE]') continue

        try {
          const data = JSON.parse(dataStr)
          if (data.type === 'delta' && data.text) {
            result += data.text
          }
        } catch (e) {
          // Skip parse errors
        }
      }
    }

    return result.trim() || 'No response received'
  } catch (error) {
    console.error('Code analysis error:', error)
    throw error
  }
}

let completionDebounce = null
const COMPLETION_DELAY = 400

let hasRegisteredInline = false

export function registerInlineAiCompletion(monaco) {
  if (hasRegisteredInline) return
  hasRegisteredInline = true

  monaco.languages.registerInlineCompletionsProvider('javascript', {
    provideInlineCompletions: async (model, position) => {
      return new Promise((resolve) => {
        clearTimeout(completionDebounce)

        completionDebounce = setTimeout(async () => {
          try {
            const textBeforeCursor = model.getValueInRange({
              startLineNumber: 1,
              startColumn: 1,
              endLineNumber: position.lineNumber,
              endColumn: position.column
            })

            const textAfterCursor = model.getValueInRange({
              startLineNumber: position.lineNumber,
              startColumn: position.column,
              endLineNumber: model.getLineCount(),
              endColumn: model.getLineMaxColumn(model.getLineCount())
            })

            if (textBeforeCursor.trim().length < 10) {
              resolve({ items: [] })
              return
            }

            const completion = await getCodeCompletion(
              textBeforeCursor,
              textAfterCursor,
              'javascript'
            )

            if (completion) {
              resolve({
                items: [
                  {
                    insertText: completion,
                    range: {
                      startLineNumber: position.lineNumber,
                      startColumn: position.column,
                      endLineNumber: position.lineNumber,
                      endColumn: position.column
                    }
                  }
                ]
              })
            } else {
              resolve({ items: [] })
            }
          } catch (error) {
            console.log('Inline completion error:', error)
            resolve({ items: [] })
          }
        }, COMPLETION_DELAY)
      })
    },
    freeInlineCompletions: () => {},
    handleItemDidShow: () => {},
    handlePartialAccept: () => {},
    disposeInlineCompletions: () => {}
  })
}

export function registerAiContextMenuActions(editor, { onShowHelpModal, showToast }) {
  editor.addAction({
    id: 'ai-explain-code',
    label: '✨ AI: Explain Selection',
    contextMenuGroupId: 'ai',
    contextMenuOrder: 1,
    run: async (ed) => {
      const selection = ed.getSelection()
      const selectedText = ed.getModel().getValueInRange(selection)

      if (!selectedText || selectedText.trim().length === 0) {
        showToast('Please select some code first', 'info')
        return
      }

      showToast('Getting AI explanation...', 'info')

      try {
        const explanation = await explainCode(selectedText.trim(), 'explain')

        onShowHelpModal({
          title: 'Code Explanation',
          content: explanation,
          code: selectedText.trim()
        })

        showToast('✅ Explanation ready', 'success')
      } catch (error) {
        console.error('Explain error:', error)
        showToast(`Failed: ${error.message}`, 'error')
      }
    }
  })

  editor.addAction({
    id: 'ai-fix-code',
    label: '🔧 AI: Fix/Improve Selection',
    contextMenuGroupId: 'ai',
    contextMenuOrder: 2,
    run: async (ed) => {
      const selection = ed.getSelection()
      const selectedText = ed.getModel().getValueInRange(selection)

      if (!selectedText || selectedText.trim().length === 0) {
        showToast('Please select some code first', 'info')
        return
      }

      showToast('Getting AI fix suggestions...', 'info')

      try {
        const fix = await explainCode(selectedText.trim(), 'fix')

        onShowHelpModal({
          title: 'Fix & Improve Code',
          content: fix,
          code: selectedText.trim()
        })

        showToast('✅ Fix suggestions ready', 'success')
      } catch (error) {
        console.error('Fix error:', error)
        showToast(`Failed: ${error.message}`, 'error')
      }
    }
  })

  editor.addAction({
    id: 'ai-optimize-code',
    label: '⚡ AI: Optimize Selection',
    contextMenuGroupId: 'ai',
    contextMenuOrder: 3,
    run: async (ed) => {
      const selection = ed.getSelection()
      const selectedText = ed.getModel().getValueInRange(selection)

      if (!selectedText || selectedText.trim().length === 0) {
        showToast('Please select some code first', 'info')
        return
      }

      showToast('Getting AI optimization...', 'info')

      try {
        const optimized = await explainCode(selectedText.trim(), 'optimize')

        onShowHelpModal({
          title: 'Code Optimization',
          content: optimized,
          code: selectedText.trim()
        })

        showToast('✅ Optimization ready', 'success')
      } catch (error) {
        console.error('Optimize error:', error)
        showToast(`Failed: ${error.message}`, 'error')
      }
    }
  })
}
